import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { useTestExecutionSummary } from '../../hooks/useDashboard';
import Spinner from '../ui/Spinner';

const STATUS_COLORS: Record<string, string> = {
  PASS: '#10B981',
  FAIL: '#EF4444',
  BLOCKED: '#F59E0B',
  SKIP: '#9CA3AF',
};

interface Props {
  projectId: string;
}

export default function TestExecutionDonutWidget({ projectId }: Props) {
  const { data, isLoading } = useTestExecutionSummary(projectId);

  if (isLoading) {
    return <div className="flex items-center justify-center h-full"><Spinner className="size-6" /></div>;
  }

  if (!data || data.total === 0) {
    return (
      <div className="h-full flex flex-col">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">테스트 실행 현황</h3>
        <div className="flex-1 flex items-center justify-center text-sm text-gray-400">테스트케이스 없음</div>
      </div>
    );
  }

  // 안쪽 도넛: 실행/미실행
  const innerData = [
    { name: '실행', value: data.executed, color: '#3B82F6' },
    { name: '미실행', value: data.notRun, color: '#E5E7EB' },
  ];

  // 바깥쪽 도넛: PASS/FAIL/BLOCKED/SKIP
  const outerData = data.statusBreakdown.map((s) => ({
    name: s.status,
    value: s.count,
    color: STATUS_COLORS[s.status] ?? '#D1D5DB',
  }));

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-700">테스트 실행 현황</h3>
        <span className="text-sm text-gray-500">실행률 {data.executionRate}%</span>
      </div>
      <div className="flex-1 relative">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            {/* 안쪽 도넛: 실행률 */}
            <Pie
              data={innerData}
              cx="50%"
              cy="50%"
              innerRadius="25%"
              outerRadius="42%"
              dataKey="value"
            >
              {innerData.map((entry, i) => (
                <Cell key={i} fill={entry.color} />
              ))}
            </Pie>
            {/* 바깥쪽 도넛: 상태 분포 */}
            <Pie
              data={outerData}
              cx="50%"
              cy="50%"
              innerRadius="50%"
              outerRadius="70%"
              dataKey="value"
            >
              {outerData.map((entry, i) => (
                <Cell key={i} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 8 }}
              formatter={(value, name) => [`${value}건`, name as string]}
            />
          </PieChart>
        </ResponsiveContainer>
        {/* 중앙 텍스트 */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <div className="text-lg font-bold text-gray-900">{data.executionRate}%</div>
            <div className="text-[11px] text-gray-500">실행률</div>
          </div>
        </div>
      </div>
      {/* 범례 */}
      <div className="flex items-center justify-center gap-3 mt-1">
        {outerData.map((d) => (
          <div key={d.name} className="flex items-center gap-1">
            <span className="size-2 rounded-full" style={{ backgroundColor: d.color }} />
            <span className="text-[11px] text-gray-600">{d.name} {d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
